import { StatusBadge, Icons, type LucideIcon } from '../ui';

/**
 * Maps a run / agent / approval / connection status (or risk level) to a *semantic*
 * tone + a small leading icon, rendered via the `@/ui` `StatusBadge`. This is the
 * Chakra-era successor to the MUI `StatusChip` for the redesigned surfaces — it keeps
 * the exact same tone semantics established in Phase 1 so state reads consistently:
 *
 *   - info     — in-flight / live work: running, queued, waiting
 *   - success  — terminal-good: completed, active, approved
 *   - warning  — needs attention: awaiting approval, limit reached, medium risk
 *   - danger   — failure: failed, blocked, rejected, high risk
 *   - neutral  — inactive: cancelled, disabled, archived, paused, expired, low risk
 *
 * Colour is never the only cue: every badge keeps its text label + a category icon,
 * and neutral states are differentiated from one another by their icon.
 */
type Tone = 'info' | 'success' | 'warning' | 'danger' | 'neutral';

interface Treatment {
  tone: Tone;
  icon: LucideIcon;
}

const TREATMENT: Record<string, Treatment> = {
  // run statuses
  COMPLETED: { tone: 'success', icon: Icons.CheckCircle2 },
  RUNNING: { tone: 'info', icon: Icons.RefreshCw },
  QUEUED: { tone: 'info', icon: Icons.Clock },
  WAITING: { tone: 'info', icon: Icons.Hourglass },
  WAITING_FOR_APPROVAL: { tone: 'warning', icon: Icons.Hourglass },
  BLOCKED: { tone: 'danger', icon: Icons.Ban },
  FAILED: { tone: 'danger', icon: Icons.AlertCircle },
  CANCELLED: { tone: 'neutral', icon: Icons.XCircle },
  LIMIT_EXCEEDED: { tone: 'danger', icon: Icons.AlertCircle },
  LIMIT_REACHED: { tone: 'warning', icon: Icons.AlertTriangle },
  // agent statuses
  active: { tone: 'success', icon: Icons.CheckCircle2 },
  paused: { tone: 'neutral', icon: Icons.PauseCircle },
  disabled: { tone: 'neutral', icon: Icons.Ban },
  archived: { tone: 'neutral', icon: Icons.Archive },
  // approval statuses
  pending: { tone: 'warning', icon: Icons.Hourglass },
  approved: { tone: 'success', icon: Icons.CheckCircle2 },
  rejected: { tone: 'danger', icon: Icons.XCircle },
  expired: { tone: 'neutral', icon: Icons.History },
  // tool-result status
  ok: { tone: 'success', icon: Icons.CheckCircle2 },
  error: { tone: 'danger', icon: Icons.AlertCircle },
  // risk levels — graduated, not all-gray
  low: { tone: 'neutral', icon: Icons.Circle },
  medium: { tone: 'warning', icon: Icons.AlertTriangle },
  high: { tone: 'danger', icon: Icons.AlertCircle },
};

/** Humanise a `SCREAMING_SNAKE` / lowercase status into a readable, sentence-case label. */
function humanize(status: string): string {
  const words = status.replace(/_/g, ' ').toLowerCase();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

export interface RunStatusBadgeProps {
  status: string;
  size?: 'sm' | 'md';
}

/** Semantic status pill for a run/agent/approval status. Keeps the `status-chip` testid. */
export function RunStatusBadge({ status, size = 'md' }: RunStatusBadgeProps) {
  const treatment = TREATMENT[status];
  return (
    <StatusBadge
      tone={treatment?.tone ?? 'neutral'}
      icon={treatment?.icon}
      label={humanize(status)}
      size={size}
      data-testid="status-chip"
    />
  );
}
